import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from 'tss-react/mui'
import type { Theme } from '@mui/material/styles'
import { Paper, Button, TextField } from '@mui/material'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import type { FieldValues } from 'react-hook-form'
import placeholder from 'config/placeholder'
import Stack from 'ui/layout/Stack'
import validationSchema from 'ui/shorthands/validationSchema'

import { ContactEmails } from 'types/med'
import { Text1 } from 'ui/shorthands/Texts'
import MarginRightCancelButton from 'ui/shorthands/MarginRightCancelButton'

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    margin: 'auto',
  },
  inputWrapper: {
    flexDirection: 'column',
  },
  text: {
    maxWidth: 640,
    paddingTop: theme.spacing(1),
  },
}))

type ForgotPasswordResultType = 'success' | 'error' | 'not_found'
export type ForgotPasswordErrorProps = { type: ForgotPasswordResultType }

export type AddEmailModalContentProps = {
  emails: ContactEmails
  onSubmit: (data: ContactEmails) => Promise<void | { message: string }>
  onClose: () => void
}

const AddEmailModalContent: React.FC<AddEmailModalContentProps> = ({
  emails,
  onSubmit,
  onClose,
}: AddEmailModalContentProps) => {
  const { t } = useTranslation()
  const { classes } = useStyles()

  const [disabled, setDisabled] = useState<boolean>()

  const defaultValues: { email: string } = { email: '' }

  const schema = yup.object().shape({
    email: validationSchema
      .email()
      .test(
        'duplication',
        t('validation.このメールアドレスはすでに登録されています'),
        (mail) => {
          if (typeof mail !== 'string') return false
          return !emails.map(({ email }) => email).includes(mail)
        }
      ),
  })

  const {
    handleSubmit,
    control,
    formState: { isValid, isSubmitting, errors },
  } = useForm<FieldValues>({
    defaultValues,
    resolver: yupResolver(schema),
    mode: 'onChange',
  })

  return (
    <Paper className={classes.root}>
      <form
        onSubmit={handleSubmit((data: FieldValues) => {
          setDisabled(true)
          onSubmit([...emails, data] as { email: string }[]).then(() => {
            setDisabled(false)
            onClose()
          })
        })}
      >
        <Stack borderBottom className={classes.inputWrapper}>
          <Controller
            render={({ field }) => (
              <TextField
                {...field}
                variant="outlined"
                placeholder={placeholder().email}
                error={!!errors.email}
                helperText={errors.email?.message}
                fullWidth
              />
            )}
            control={control}
            name={'email'}
          />
          <Text1 className={classes.text}>
            {t(
              '登録後、完了メールが届かない場合は、メールアドレスをご確認のうえ、一度削除してから再登録してください。'
            )}
          </Text1>
        </Stack>
        <Stack align={'right'}>
          <MarginRightCancelButton onClick={onClose} />
          <Button
            color="primary"
            variant="contained"
            type="submit"
            disabled={!isValid || isSubmitting || disabled}
          >
            {t('保存')}
          </Button>
        </Stack>
      </form>
    </Paper>
  )
}

export default AddEmailModalContent
